/* eslint no-console: 0 */

import Vue from 'vue';
import VueMoment from 'vue-moment/dist/vue-moment.es';
import Rollbar from 'vue-rollbar';
import { camelCase, isNil, last, split, upperFirst } from 'lodash';
import VueHotkey from 'v-hotkey';
import VueScroll from 'vue-scroll';
import VueNotification from 'vue-notification';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import VueHead from 'vue-head';
import VueSVGIcon from 'vue-svgicon';
import VueAxios from 'vue-axios';
import axios from './axios';
import VueTimers from 'vue-timers';
import vClickOutside from 'v-click-outside';
import VueGoogleCharts from 'vue-google-charts';
import PortalVue from 'portal-vue';
import { VueMaskDirective } from 'v-mask';
import Popper from '@/plugins/popper-js';
import SkyLightbox from 'sky-lightbox';
import VueLogger from 'vuejs-logger';
import velocity from 'velocity-animate';
import { isProduction } from './isProduction';
import VCalendar from 'v-calendar';
import vSelect from 'vue-select';
import moment from './moment';
import Multiselect from 'vue-multiselect';

export default () => {
  Vue.config.productionTip = false;
  Vue.config.devtools = !isProduction;
  Vue.config.performance = !isProduction;
  Vue.config.ignoredElements = [/^seam-/];

  Vue.use(VueMoment, {
    moment,
  });

  Vue.use(Rollbar, {
    accessToken: '84ed6e6cd229417c87bbf000fb4f180b',
    captureUncaught: true,
    autoInstrument: { network: false, log: true, dom: true, navigation: true, connectivity: true },
    captureUnhandledRejections: true,
    enabled: process.env.HOTELMEISTER_ENV !== 'development',
    environment: process.env.HOTELMEISTER_ENV,
    captureIp: 'anonymize',
    hostBlackList: ['localhost'],
    payload: {
      client: {
        javascript: {
          code_version: window.HM_VERSION,
          source_map_enabled: true,
          guess_uncaught_frames: true,
        },
      },
      environment: process.env.HOTELMEISTER_ENV,
    },
    ignoredMessages: [
      'Failed to update a ServiceWorker for scope',
      'Request failed with status code 401',
      'UNKNOWN for http[s]?://[^/]+/rest/ui/backend/echo/keepAlive',
      '.*NavigationDuplicated:*',
      '.*Navigation cancelled from.*',
      '.*Avoided redundant navigation to.*',
      'avoided redundant navigation to current location.*',
      'Script error.',
      'Cannot read properties of null (reading \'contains\')',
      '.*Cannot read properties of null.*',
      'Uncaught TypeError: Cannot read properties of null (reading \'contains\')',
      'Error: AUTH TOKEN INVALID OR MISSING',
      'AUTH TOKEN INVALID OR MISSING',
      '.*FetchEvent.respondWith received an error: TypeError:.*',
      '.*ServiceWorker.*',
      '.*Failed to fetch.*',
      'Network Error',
      'NetworkError.*',
      'Received response with status UNKNOWN.*',
    ],
  });

  Vue.config.errorHandler = function (err, vm, info) {
    if (!isNil(err) && err.name !== 'NavigationDuplicated') {
      vm.$rollbar.error(err);
    }
    console.error(err);
    vm.axios.post(vm.$serviceUrls.log.error, { error: err, info: info, user: vm.$root.username, url: window.location.href, hotel: vm.$root.hotel, date: new Date().toISOString() });
  };

  Vue.use(Popper);
  Vue.use(VueHotkey, {
    ß: 219,
    '/': 191,
    n0: 48,
    n1: 49,
    n2: 50,
    n3: 51,
    n4: 52,
    n5: 53,
    n6: 54,
    n7: 55,
    n8: 56,
    n9: 57,
  });
  Vue.use(VueScroll, { debounce: 100 });
  Vue.use(VueNotification, { velocity });
  Vue.use(Vuex);
  Vue.use(VueRouter);
  Vue.use(VueHead);
  Vue.use(VueSVGIcon);
  Vue.use(VueAxios, axios);
  Vue.use(VueTimers);
  Vue.use(vClickOutside);
  Vue.use(VueGoogleCharts);
  Vue.use(PortalVue);

  Vue.directive('mask', VueMaskDirective);

  Vue.use(SkyLightbox, {
    // Use `alt` attribute as gallery slide title
    altAsTitle: false,
    // Event listener to open gallery will be applied to <img> element
    openOn: 'click',
  });

  // Use v-calendar, v-date-picker & v-popover components
  Vue.use(VCalendar, {
  });

  Vue.component('v-select', vSelect);
  Vue.component('v-multiselect', Multiselect);

  const requireTransition = require.context(
    // The relative path of the components folder
    '../../transitions',
    // Whether or not to look in subfolders
    true,
    // The regular expression used to match base component filenames
    /.*\.(js|vue)$/,
  );

  requireTransition.keys().forEach((fileName) => {
    // Get component config
    const transitionConfig = requireTransition(fileName);

    const baseFileName = last(split(fileName, '/'));

    // Get PascalCase name of component
    const transitionName = camelCase(
      // Strip the leading `./` and extension from the filename
      // baseFileName.replace(/^\.\/(.*)\.\w+$/, '$1'),
      baseFileName.replace(/^(.*)\.\w+$/, '$1'),
    );

    // Register component globally
    Vue.component(
      transitionName,
      // Look for the component options on `.default`, which will
      // exist if the component was exported with `export default`,
      // otherwise fall back to module's root.
      transitionConfig.default || transitionConfig,
    );
  });


  const requireComponent = require.context(
    // The relative path of the components folder
    '../../components/lib',
    // Whether or not to look in subfolders
    true,
    // The regular expression used to match base component filenames
    /.*\.(vue|js)$/,
  );

  requireComponent.keys().forEach((fileName) => {
    // Get component config
    const componentConfig = requireComponent(fileName);

    const baseFileName = last(split(fileName, '/'));

    // Get PascalCase name of component
    const componentName = upperFirst(
      camelCase(
        // Strip the leading `./` and extension from the filename
        // baseFileName.replace(/^\.\/(.*)\.\w+$/, '$1'),
        baseFileName.replace(/^(.*)\.\w+$/, '$1'),
      ),
    );

    // Register component globally
    Vue.component(
      componentName,
      // Look for the component options on `.default`, which will
      // exist if the component was exported with `export default`,
      // otherwise fall back to module's root.
      componentConfig.default || componentConfig,
    );
  });


  const options = {
    isEnabled: true,
    logLevel: isProduction ? 'error' : 'debug',
    stringifyArguments: false,
    showLogLevel: false,
    showMethodName: false,
    separator: '|',
    showConsoleColors: false,
  };

  Vue.use(VueLogger, options);
};



// WEBPACK FOOTER //
// ./src/lib/util/vue.js